import React, { useState, useEffect } from "react";
import { FaPhone, FaSms, FaShieldAlt, FaClock, FaLock, FaArrowRight } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { getOTp, verifyOtp } from "../redux/actions/userActions";
import { toast } from "../utils/toast";
import Loading from './Loading';

function VerifyOtp() {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(270); // 4:30 minutes
  const [canResend, setCanResend] = useState(false);
  
  // Redux setup
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redux state
  const { loading: otpLoading, success: otpSuccess, error: otpError } = useSelector(state => state.getOtp);
  const { loading: verifyLoading, success: verifySuccess, error: verifyError } = useSelector(state => state.verifyOtp);
  
  // Get file_number from location state or localStorage
  const fileNumber = location.state?.system_id || localStorage.getItem('temp_file_number') || '';
  console.log('location', location);

  // Timer effect
  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Handle OTP success
  useEffect(() => {
    if (otpSuccess) {
      setStep(2);
      setTimer(270); // Reset timer
      setCanResend(false);
      toast.success('OTP sent successfully!');
      // Reset OTP state
      dispatch({ type: 'GET_OTP_RESET' });
    }
  }, [otpSuccess, dispatch]);

  // Handle OTP error
  useEffect(() => {
    if (otpError) {
      toast.error(otpError);
      dispatch({ type: 'GET_OTP_RESET' });
    }
  }, [otpError, dispatch]);

  // Handle verify success
  useEffect(() => {
    if (verifySuccess) {
      toast.success('Phone number verified successfully!');
      let cleanPhone2 = phoneNumber.replace(/\D/g, '');
      localStorage.setItem('phone', JSON.stringify(cleanPhone2) );
      localStorage.setItem('file_number', JSON.stringify(fileNumber));
      navigate('/createpassword');
    }
  }, [verifySuccess, navigate]);

  // Handle verify error
  useEffect(() => {
    if (verifyError) {
      toast.error(verifyError);
    }
  }, [verifyError]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    
    if (!fileNumber) {
      toast.error('File number is required');
      return;
    }
    
    if (phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Clean phone number (remove formatting)
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    dispatch(getOTp(fileNumber, cleanPhone));
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 4) {
      toast.error('Please enter the complete 4-digit code');
      return;
    }

    const cleanPhone = phoneNumber.replace(/\D/g, '');
    dispatch(verifyOtp(fileNumber, cleanPhone, otp));
  };

  const handleResend = () => {
    if (!canResend) return;
    
    setTimer(270);
    setCanResend(false);
    setOtp('');
    
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    dispatch(getOTp(fileNumber, cleanPhone));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 11)}`;
  };

  const handleClose = () => {
    dispatch({ type: 'GET_OTP_RESET' });
    dispatch({ type: 'VERIFY_OTP_RESET' });
    navigate('/');
  };

  const isLoading = otpLoading || verifyLoading;

  return (
    <div className="min-h-screen bg-primary100 flex flex-col justify-center items-center p-4">
      {/* Loading Overlay */}
      {isLoading && (
        <Loading 
          variant="medical" 
          size="large" 
          message={otpLoading ? "Sending verification code..." : "Verifying code..."} 
          fullScreen={true} 
        />
      )}

      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-primary100 px-8 py-12 text-center relative">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="relative">
            <div className="w-20 h-20 bg-white rounded-full flex justify-center items-center mx-auto mb-4 shadow-lg">
              {step === 1 ? (
                <FaPhone className="text-primary100 text-2xl" />
              ) : (
                <FaSms className="text-primary100 text-2xl" />
              )}
            </div>
            {/* Animated rings */}
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="w-28 h-28 border-2 border-white/30 rounded-full animate-ping"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {step === 1 ? "Verify Phone Number" : "Enter Verification Code"}
          </h1>
          <p className="text-blue-100 text-sm">
            {step === 1 
              ? "Enter your phone number to receive a verification code"
              : `Enter the 4-digit code sent to ${phoneNumber}`
            }
          </p>
          {fileNumber && (
            <p className="text-blue-200 text-xs mt-2">
              File Number: {fileNumber}
            </p>
          )}
        </div>

        {/* Security Features */}
        <div className="px-8 py-4 bg-blue-50">
          <div className="flex justify-center space-x-6">
            <div className="flex items-center space-x-2">
              <FaShieldAlt className="text-green-500 text-sm" />
              <span className="text-xs text-gray-600">Secure</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaLock className="text-green-500 text-sm" />
              <span className="text-xs text-gray-600">Encrypted</span>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="px-8 py-8">
          {step === 1 ? (
            // Phone Number Form
            <form onSubmit={handlePhoneSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  placeholder="08012345678"
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-primary100 focus:ring-2 focus:ring-primary100/20 transition-all duration-200 text-lg"
                  maxLength="16"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  We'll send a verification code to this number
                </p>
              </div>

              <button
                type="submit"
                disabled={phoneNumber.length < 10 || isLoading}
                className="w-full bg-primary100 text-white py-4 rounded-lg font-semibold text-lg hover:opacity-90 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:transform-none flex items-center justify-center space-x-2"
              >
                <span>Send Verification Code</span>
                <FaArrowRight />
              </button>
            </form>
          ) : (
            // OTP Form
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="0000"
                  className="w-full px-4 py-4 text-center text-2xl font-bold tracking-widest border-2 border-gray-200 rounded-lg focus:border-primary100 focus:ring-2 focus:ring-primary100/20 transition-all duration-200"
                  maxLength="4"
                  required
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Enter the 4-digit code sent to your phone
                </p>
              </div>

              {/* Timer Display */}
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FaClock className="text-primary100" />
                  <span className="font-semibold text-gray-700">Time Remaining</span>
                </div>
                <div className="text-2xl font-bold text-primary100">
                  {formatTime(timer)}
                </div>
                {canResend && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isLoading}
                    className="mt-3 text-primary100 hover:text-blue-600 font-semibold text-sm underline transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Resend Code
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={otp.length !== 4 || isLoading}
                  className="w-full bg-primary100 text-white py-4 rounded-lg font-semibold text-lg hover:opacity-90 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:transform-none flex items-center justify-center space-x-2"
                >
                  <span>Verify Code</span>
                  <FaArrowRight />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                    setTimer(270);
                    setCanResend(false);
                  }}
                  disabled={isLoading}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Change Phone Number
                </button>
              </div>
            </form>
          )}

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              {step === 1 ? (
                <>
                  By continuing, you agree to our{" "}
                  <span className="text-primary100 hover:underline cursor-pointer">
                    Terms of Service
                  </span>
                </>
              ) : (
                <>
                  Didn't receive the code? Check your messages or{" "}
                  <span className="text-primary100 hover:underline cursor-pointer">
                    contact support
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyOtp;
